'use client'

import useModal from '@components/layout/Modal'
import UIFlex from '@components/ui/UIFlex'
import useIsMounted from '@hooks/useIsMounted'
import useMobileDisabled from '@hooks/useMobileDisabled'
import AspectRatio from '@mui/joy/AspectRatio'
import Box from '@mui/joy/Box'
import pxToRem from '@utils/pxToRem'
import { CSSProperties, ReactNode, useCallback, useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { appInterface } from '@store/isApp'
import IconArticleVideoPlay from 'src/assets/icons/IconArticleVideoPlay'
import { useConfirmControl } from '@store/confirm'
import UIButton from '@components/ui/UIButton'

interface VideoContainerProps {
	/** 동영상 비율 @default '16/9' */
	ratio?: string
	children: ReactNode
	isVodPlaying?: boolean
	isIframe?: boolean
	vodPlayer?: JTBCVideoPlayerWrapper
}
type PIPInit = {
	position?: 'absolute' | 'fixed'
	bottom?: number | string
	right?: number | string
	width?: number | string
	height?: number | string
}
const PIP_INIT: PIPInit = {
	position: 'absolute',
	bottom: 0,
	right: 0,
	width: '100%',
	height: '100%',
}
const VideoContainer = ({
	ratio = '16/9',
	children,
	isVodPlaying = false,
	isIframe = false,
	vodPlayer,
}: VideoContainerProps) => {
	const [vodObserver, setVodObserver] = useState<MutationObserver>()
	const [PIPInfo, setPIPInfo] = useState(PIP_INIT)
	const nativeEvent = useRecoilValue(appInterface)
	const mobileDisabled = useMobileDisabled()
	const isMounted = useIsMounted()
	const { netWork: netWorkConfirm } = useModal()
	const { closeConfirm } = useConfirmControl()
	const handlerNetWorkConfirm = () => {
		if (isIframe) {
			const frame = document.querySelector('.video-container-iframe')
			const src = frame?.getAttribute('src')?.replace('autoplay=1&', '')
			if (src) frame?.setAttribute('src', src)
		}
		nativeEvent({ key: 'requestDeviceIsMobile', value: null })
		if (mobileDisabled) netWorkConfirm()
	}
	const handlerTogglePIP = () => {
		vodPlayer?.onTogglePIP()
	}
	const handlerHiddenPlayIcon = (): CSSProperties => {
		return { position: 'absolute', width: '100%', height: '100%' }
	}
	/** video Observer, video Tag 생성 후 재생 일시정지 여부 Event 수신 */
	const handleVodObserve = useCallback((mutationList: Array<MutationRecord>) => {
		const mutation = mutationList.pop() as MutationRecord
		const target = mutation.target as HTMLElement
		if (target?.id !== 'jtbc-video-player') return
		if ([...target.classList].includes('pip-mode')) {
			const { width, height, bottom, right } = getComputedStyle(target)
			setPIPInfo({
				position: 'fixed',
				bottom,
				right,
				width,
				height,
			})
		} else {
			setPIPInfo(PIP_INIT)
		}
	}, [])
	useEffect(() => {
		if (!mobileDisabled) {
			closeConfirm('close')
		}
	}, [closeConfirm, mobileDisabled])
	/** video Observer 셋팅  */
	useEffect(() => {
		if (!vodObserver) {
			setVodObserver(new MutationObserver(handleVodObserve))
		}
		if (vodPlayer && vodObserver) {
			vodObserver.observe(vodPlayer.container, {
				subtree: true,
				attributeOldValue: true,
			})
		}
		return () => {
			vodObserver?.disconnect()
		}
	}, [handleVodObserve, vodObserver, vodPlayer])

	return (
		<AspectRatio
			variant="outlined"
			ratio={ratio}
			sx={(theme) => ({
				bgcolor: theme.color.colBlack,
				'& .MuiAspectRatio-content': {
					border: 0,
					'& iframe': {
						width: '100%',
						height: '100%',
					},
				},
				'& .jtbcplayer-container': {
					aspectRatio: ratio,
				},
				'& .jtbcplayer-container .tts-video-overlay svg': {
					display: 'block',
					width: '34px',
					height: '34px',
				},
				'& .jtbcplayer-container.vertical .tts-video-overlay svg': {
					display: 'block',
					width: '50px',
					height: '50px',
				},
				'& .jtbcplayer-container.ended .tts-video-overlay svg': {
					display: 'none',
				},
			})}
		>
			<Box
				suppressHydrationWarning
				sx={(theme) => ({
					pointerEvents: mobileDisabled && !isVodPlaying ? 'none' : 'unset',
					'& .jtbcplayer-container.pip-mode': {
						transition: 'right 0.25s',
						zIndex: '100',
					},
					[theme.breakpoints.up('md')]: {
						'& .open-drawer .jtbcplayer-container.pip-mode': {
							right: `${pxToRem(446 + 48)}!important`,
						},
					},
					[theme.breakpoints.up('xl')]: {
						'& .open-drawer .jtbcplayer-container.pip-mode': {
							right: `${pxToRem(560 + 48)}!important`,
						},
					},
				})}
			>
				{children}
			</Box>
			{isMounted && mobileDisabled && !isVodPlaying && (
				<UIFlex
					position={PIPInfo.position}
					bottom={PIPInfo.bottom}
					right={PIPInfo.right}
					width={PIPInfo.width}
					height={PIPInfo.height}
					border={0}
					zIndex={100}
					alignItems="center"
					justifyContent="center"
				>
					{PIPInfo?.position === 'fixed' && (
						<UIButton
							variant="text"
							sx={{
								'&': {
									position: 'absolute',
									top: '6px',
									right: '-2px',
								},
							}}
							onClick={handlerTogglePIP}
						>
							<svg
								width="24"
								height="24"
								viewBox="0 0 25 24"
								fill="rgba(255, 255, 255, 1.0)"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M22.1154 19.9102L4.62836 2.42319C4.39471 2.18954 4.03313 2.17229 3.82071 2.3847L2.92322 3.28219C2.71081 3.49461 2.72806 3.85619 2.96171 4.08984L20.4487 21.5769C20.6824 21.8105 21.0439 21.8278 21.2564 21.6154L22.1539 20.7179C22.3663 20.5055 22.349 20.1439 22.1154 19.9102Z"
									fill="current"
								/>
								<path
									d="M4.6285 21.5768L22.1155 4.08972C22.3492 3.85607 22.3664 3.49449 22.154 3.28207L21.2565 2.38458C21.0441 2.17217 20.6825 2.18942 20.4489 2.42307L2.96184 19.9101C2.72818 20.1438 2.71093 20.5053 2.92334 20.7178L3.82084 21.6153C4.03325 21.8277 4.39483 21.8104 4.6285 21.5768Z"
									fill="current"
								/>
							</svg>
						</UIButton>
					)}
					<UIButton
						variant="text"
						onClick={handlerNetWorkConfirm}
						style={(isIframe && handlerHiddenPlayIcon()) || {}}
					>
						{!isIframe && <IconArticleVideoPlay className="icon-md" size="md" />}
					</UIButton>
				</UIFlex>
			)}
		</AspectRatio>
	)
}

export default VideoContainer
